import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, ApiError } from "../lib/api.js";
import { Button } from "../components/Button.js";

/**
 * Duplicate a form into a new one with no submissions — "the once-a-year workflow, and it must be
 * one click" (docs/04-BUILD-PLAN.md Phase 3).
 *
 * An inline strip under the row rather than a modal: it is the pattern the delete confirmation next
 * to it already uses, and it keeps the form you are copying on screen while you name its copy.
 *
 * The three things it asks for are the three that cannot be inferred. Everything else — questions,
 * logic, branding, dedupe rules, the scoring rubric, the form's own emails — comes across on the
 * server, and the things that must not (submissions, reference-number counters, the open/close
 * window, webhook secrets) are left behind there too.
 */

interface Cycle {
  id: string;
  label: string;
  programmeId: string;
  isCurrent: boolean;
}

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);

export const DuplicateFormStrip = ({
  form,
  onCancel,
  onDuplicated,
}: {
  form: { id: string; title: string; programmeId: string; cycleId: string | null };
  onCancel: () => void;
  onDuplicated: (newFormId: string) => void;
}): JSX.Element => {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(`${form.title} (copy)`);
  const [slug, setSlug] = useState(() => slugify(`${form.title} copy`));
  // "" means "the same cycle as the original" — the server reads an absent cycle that way.
  const [cycleId, setCycleId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  useEffect(() => titleRef.current?.select(), []);

  // Mounted only while the strip is open, so opening the Forms page does not fetch cycles for every
  // programme in the list.
  const cyclesQuery = useQuery({
    queryKey: ["cycles", form.programmeId],
    queryFn: () => apiGet<{ data: Cycle[] }>(`/cycles?programmeId=${form.programmeId}`),
  });

  const duplicate = useMutation({
    mutationFn: () =>
      apiPost<{ id: string }>(`/forms/${form.id}/duplicate`, {
        newTitle: title,
        newSlug: slug,
        ...(cycleId ? { targetCycleId: cycleId } : {}),
      }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ["forms"] });
      onDuplicated(created.id);
    },
    onError: (caught) => {
      setError(caught instanceof ApiError ? caught.message : "This form could not be duplicated.");
    },
  });

  const cycles = cyclesQuery.data?.data ?? [];

  return (
    <form
      className="space-y-2 border-t bg-muted/40 px-4 py-3"
      onSubmit={(event) => {
        event.preventDefault();
        setError(null);
        if (title && slug) duplicate.mutate();
      }}
    >
      <p className="text-xs text-muted-foreground">
        Copies the questions, logic, branding and rules of <strong>{form.title}</strong> into a new
        draft. Its submissions and reference numbers stay behind.
      </p>

      <div className="flex flex-wrap items-end gap-2">
        <label className="min-w-[14rem] flex-1">
          <span className="mb-1 block text-[11px] uppercase text-muted-foreground">Title</span>
          <input
            ref={titleRef}
            className="w-full rounded-md border px-2 py-1.5 text-sm"
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
              setSlug(slugify(event.target.value));
            }}
            required
          />
        </label>

        <label>
          <span className="mb-1 block text-[11px] uppercase text-muted-foreground">Cycle</span>
          <select
            className="rounded-md border px-2 py-1.5 text-sm"
            value={cycleId}
            onChange={(event) => setCycleId(event.target.value)}
            disabled={cyclesQuery.isLoading}
          >
            <option value="">Same as original</option>
            {cycles.map((cycle) => (
              <option key={cycle.id} value={cycle.id}>
                {cycle.label}
                {cycle.isCurrent ? " (current)" : ""}
              </option>
            ))}
          </select>
        </label>

        <label className="min-w-[12rem] flex-1">
          <span className="mb-1 block text-[11px] uppercase text-muted-foreground">Public URL</span>
          <span className="flex items-center rounded-md border bg-card pl-2 text-sm">
            <span className="text-muted-foreground">/f/</span>
            <input
              className="w-full bg-transparent px-1 py-1.5 text-sm outline-none"
              value={slug}
              onChange={(event) => setSlug(event.target.value)}
              pattern="[a-z0-9][a-z0-9-]*"
              title="Lowercase letters, numbers and hyphens"
              required
            />
          </span>
        </label>

        <Button variant="primary" type="submit" disabled={duplicate.isPending}>
          {duplicate.isPending ? "Duplicating…" : "Duplicate and open"}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>

      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </form>
  );
};
